/**
 * The docs page in French (Belgian usage, with "vous"). Claude's menu names
 * and buttons stay as Claude shows them, in English.
 *
 * Write a plain space before `: ; ! ?` and `»` and after `«`: `frenchSpacing`
 * makes each one a no-break space, so a line never starts with one.
 */

import { frenchSpacing, mapStrings } from "../utils";
import type { DocsContent } from "./types";

const text: DocsContent = {
  page: {
    title: "Documentation",
    description: "Comment utiliser le connecteur DC Data dans Claude.",
    contentsLabel: "Sommaire",
    pagerLabel: "Pages précédente et suivante",
    previous: "Précédent",
    next: "Suivant",
  },

  pages: {
    "connect-claude": "Connecter Claude",
    "what-you-can-ask": "Ce que vous pouvez demander",
    tools: "Les outils",
    limits: "Ce qu’il ne peut pas faire",
    "privacy-and-data": "Vie privée et données",
    troubleshooting: "Questions et problèmes",
  },

  connect: {
    intro:
      "Deux étapes : une personne ayant le rôle Owner dans votre organisation Claude ajoute le connecteur une seule fois, pour tout le monde. Ensuite, chacun le connecte avec son propre compte.",
    introEveryoneFirst:
      "Vous connectez vous-même le connecteur, avec votre propre compte. Si **DC Data** n’apparaît pas encore dans votre liste, une personne ayant le rôle Owner dans votre organisation Claude l’ajoute d’abord une seule fois, pour tout le monde. Ces étapes se trouvent en bas de la page.",
    modeOff:
      "Le connecteur n’est pas encore activé. Dès qu’il le sera, ces étapes fonctionneront et l’URL à ajouter s’affichera ici.",
    modeLocal:
      "Ce connecteur fonctionne en mode de test local, sans identification, uniquement sur cet ordinateur. Ajoutez l’URL à un client MCP local, comme Claude Code ou MCP Inspector.",
    ownerHeading: "Une seule fois, par un Owner de l’organisation Claude",
    ownerSteps: [
      "Dans Claude, ouvrez **Organization settings → Connectors**, cliquez sur **Add**, puis sur **Custom → Web**.",
      "Nommez-le **DC Data** et collez cette URL :",
      "Cliquez ensuite sur **DC Data** dans la liste et, sous **Tool permissions**, réglez le groupe **Read-only tools** sur **Always allow**. Tous les outils ne font que lire : les recherches peuvent alors se faire sans approbation.",
    ],
    ownerFolded: "Pour l’Owner de l’organisation Claude",
    everyoneHeading: "Ensuite, pour tout le monde",
    everyoneFirstHeading: "Pour tout le monde, avec votre propre compte",
    everyoneSteps: [
      "Dans Claude, dans le navigateur ou l’application, ouvrez **Customize → Connectors**.",
      "Cherchez **DC Data** et cliquez sur **Connect**.",
      "Connectez-vous avec votre compte Microsoft De Ceuster, votre compte de travail habituel.",
      "Cliquez ensuite sur **DC Data** et, sous **Tool permissions**, réglez le groupe **Read-only tools** sur **Always allow**, pour que Claude ne demande pas l’autorisation à chaque question.",
      "Testez-le dans une nouvelle conversation : « Avec quel compte suis-je connecté à DC Data ? » Claude répond avec votre nom et votre adresse e-mail.",
    ],
    accessNote:
      "Toute personne disposant d’un compte Microsoft De Ceuster peut se connecter. Les comptes invités ne le peuvent pas.",
    urlLoading: "Chargement de l’URL…",
    urlNotOnYet: "L’URL s’affichera ici dès que le connecteur sera activé.",
    urlFailed: "Impossible de charger l’URL. Rechargez la page pour réessayer.",
  },

  copyUrl: {
    button: "Copier",
    copied: "Copié",
    label: "Copier l’URL du connecteur",
    copiedStatus: "L’URL du connecteur a été copiée.",
    failed: "Impossible de copier : sélectionnez l’URL et copiez-la vous-même.",
  },

  capabilities: {
    intro:
      "Le connecteur donne à Claude un accès en lecture seule à votre Odoo : clients, chantiers, grues, commandes, contrats de location, factures, paiements et chiffre d’affaires. Posez votre question simplement, en français, en néerlandais ou en anglais, comme vous la poseriez à un collègue. Claude choisit lui-même les outils et lit Odoo en direct : vous obtenez la situation du moment.",
    toolsNote:
      "Voici les outils que vous obtenez dans Claude. Avec la plupart d’entre eux, Claude lit votre Odoo. Vous n’avez pas besoin de connaître leurs noms, car Claude choisit. Ils aident toutefois à voir ce que Claude a consulté. Sur la page Outils, vous désactivez un outil pour vous-même, et Claude ne l’utilise alors plus dans vos conversations.",
    toolsLink: "Vos outils, activés ou non",
    toolsLoading: "Chargement des outils…",
    toolsFailed: "Impossible de charger les outils. Rechargez la page pour réessayer.",
    toolSeparator: " : ",
    examplesHeading: "Essayez par exemple",
    quotes: ["« ", " »"],
    examples: {
      revenue: "Quel était notre chiffre d’affaires par mois en 2025 ?",
      overdue:
        "Quelles factures de [client] sont en retard de paiement, et depuis combien de temps ?",
      "on-rent": "Lesquelles de nos grues sont en location en ce moment, et chez quels clients ?",
      rentals: "Quels contrats de location sont en cours chez [client] ?",
      deliveries: "Quelles commandes sont livrées la semaine prochaine, et sur quel chantier ?",
      map: "Placez nos chantiers du troisième trimestre sur une carte, avec des cercles autour de Geel.",
    },
    examplesNote: "Là où figure [client], indiquez le nom d’un client.",
    tipsHeading: "Conseils",
    tips: {
      period: {
        title: "Indiquez une période",
        body: "« En 2025 », « du 1er au 31 mars », « le mois dernier ». Sans période, Claude ratisse large et la réponse devient volumineuse.",
      },
      "client-first": {
        title: "D’abord le client, ensuite les détails",
        body: "Laissez d’abord Claude trouver le client, et choisissez le bon s’il y en a plusieurs. Demandez ensuite ses commandes, ses locations ou ses factures : Claude travaille ainsi avec ce client-là, et pas avec un homonyme.",
      },
      "say-what-you-mean": {
        title: "Précisez ce que vous cherchez",
        body: "Claude montre les commandes et les contrats de location confirmés ; demandez explicitement les offres. Les commandes sont recherchées par date de commande : précisez-le si vous parlez de la date de livraison.",
      },
      form: {
        title: "Demandez la forme dont vous avez besoin",
        body: "Un tableau, un graphique, une carte, ou un artefact : une page interactive à côté de la conversation.",
      },
    },
  },

  limits: {
    "changes-nothing": {
      title: "Il ne modifie rien",
      body: "Via le connecteur, Claude ne peut ni créer ni modifier de commandes, d’offres, de factures ni de clients dans Odoo, et il n’envoie rien.",
    },
    "not-everything": {
      title: "Pas tout ce qui se trouve dans Odoo",
      body: "Claude voit les paiements arrivés sur le compte bancaire et les factures qu’ils ont réglées. Un paiement qui n’est pas encore lettré avec une facture est compté à part. Cette facture reste alors ouverte dans Odoo, alors qu’elle peut déjà être payée. L’historique de propriété des grues, ainsi que le leasing et l’assurance par grue ne sont pas affichés.",
    },
    "map-approximate": {
      title: "Les positions sur la carte sont approximatives",
      body: "Odoo ne conserve pas de coordonnées pour les chantiers. Claude place donc un chantier au centre de son code postal, ou de sa commune si le code postal manque, et mesure la distance jusqu’à Geel à vol d’oiseau. Les chantiers à l’étranger, ou sans code postal ni commune utilisables, n’apparaissent pas sur la carte.",
    },
    "closed-rental-site": {
      title: "Le chantier d’une location clôturée",
      body: "Odoo remet parfois le chantier d’un contrat de location clôturé sur votre dépôt. Claude prend alors le chantier de la dernière facture.",
    },
    "no-end-date": {
      title: "Pas de date de fin réelle d’une location",
      body: "Odoo connaît le début, la fin estimée et le jour où le contrat a été clôturé dans Odoo, mais pas le jour où la grue est réellement revenue. « Quelles grues étaient en location le mois dernier » est donc une bonne approximation, pas un décompte exact.",
    },
    "occupancy-per-type": {
      title: "Taux d’occupation et disponibilité par type de grue",
      body: "Claude donne le taux d’occupation (bezettingsgraad) par jour, par semaine ou par mois à partir du 1er janvier 2024, pour toute la flotte, pour les grues à montage rapide et les grues à tour, et par type de grue. À partir d’aujourd’hui, Claude compte les grues réservées, jusqu’à un an à l’avance au plus. Claude indique aussi quelles grues sont libres sur une période et quelles alertes l’application Taux d’occupation signale. Claude calcule tout cela à partir d’Odoo au moment où vous le demandez, avec les règles de l’application Taux d’occupation, et précise quand Odoo a été lu. Claude réutilise une même lecture d’Odoo pendant 10 minutes au plus, une réservation toute récente peut donc ne compter qu’après quelques minutes. Si Odoo ne montre pas les grues, Claude le dit et ne donne aucune estimation. La hauteur et la flèche d’une grue ne figurent pas dans Odoo, la disponibilité se donne donc par type de grue et non par taille.",
    },
    "finance-checks": {
      title: "Travaux non facturés et prix de transport",
      body: "Claude peut montrer les travaux livrés mais pas ou seulement en partie facturés, et les transports facturés bien en dessous du tarif. Claude calcule les deux à partir d’Odoo au moment où vous le demandez, et précise quand Odoo a été lu. Claude réutilise une même lecture d’Odoo pendant 10 minutes au plus, une facture toute récente peut donc ne compter qu’après quelques minutes. Les travaux qui semblent facturés autrement, par exemple par une note de crédit, une autre facture ou la vente d’une grue, sont laissés de côté. Les travaux sur vos propres grues de location ne comptent pas dans ce que les clients doivent encore et sont listés à part. Odoo ne connaît pas le coût d’un transport, un prix sous le tarif n’est donc pas une perte. Seules les personnes qui ont Clients voient les noms des clients.",
    },
    "outside-odoo": {
      title: "Rien en dehors d’Odoo",
      body: "Le planning dans Visual Planner, l’application Ford, vos e-mails, et la raison pour laquelle une offre a été perdue.",
    },
    "left-out": {
      title: "Exclu volontairement",
      body: "Les coordonnées (e-mail, téléphone), les données bancaires, les données du personnel et les notes internes dans Odoo.",
    },
    "answer-size": {
      title: "Une taille limitée par réponse",
      body: "Chaque recherche récupère une quantité limitée. Si une réponse est trop volumineuse, elle est raccourcie, et Claude le signale.",
    },
  },

  privacy: {
    "own-account": {
      title: "Votre propre compte",
      body: "Chacun se connecte avec son propre compte Microsoft De Ceuster. Quand quelqu’un quitte l’entreprise, l’administrateur informatique de De Ceuster désactive son compte, et son accès prend fin dans un délai d’environ une heure. Si quelqu’un n’a plus besoin du connecteur, ou si son accès doit cesser plus tôt, un administrateur le bloque immédiatement dans Personnes et équipes, sous l’onglet Équipes. Velta peut aussi le faire pour vous.",
    },
    "same-for-everyone": {
      title: "Les mêmes outils, les mêmes données",
      body: "Le connecteur lit Odoo via un seul compte en lecture seule. Ce que chacun voit ne dépend donc pas de ses propres droits dans Odoo. Avec les mêmes outils, tout le monde voit les mêmes données Odoo, comme le score Companyweb ou le prix minimum et la marge d’une grue. Seules les personnes qui peuvent rechercher des factures, via Finances dans leur équipe ou une exception personnelle, voient les factures ouvertes par client. De même, les outils du taux d’occupation et ceux des travaux non facturés et des prix de transport ne montrent les noms des clients qu’à ceux qui ont Clients.",
    },
    "passes-through": {
      title: "Les données ne font que passer",
      body: "Le connecteur transmet les données d’Odoo à Claude et ne les conserve pas. Seule votre connexion est conservée, chiffrée, pour que vous ne deviez pas vous reconnecter à chaque fois. La réponse se trouve ensuite dans votre conversation dans Claude, comme tout ce que vous faites dans Claude.",
    },
    "log-metadata": {
      title: "Le journal ne conserve que des métadonnées",
      body: "Qui a utilisé quel outil, quand, combien de temps cela a pris et si cela a échoué, pour assurer le fonctionnement du service. Le journal conserve ces informations 12 mois, puis les supprime automatiquement. Le contenu des questions et des réponses n’est jamais conservé ni journalisé.",
    },
  },

  roles: {
    heading: "Qui voit quoi sur le tableau de bord",
    intro:
      "Chacun a un seul rôle sur ce tableau de bord, distinct de son rôle dans votre organisation Claude. À sa première connexion, chacun est Membre. Un administrateur peut ensuite modifier ce rôle dans Personnes et équipes.",
    access: "Accès",
    yes: "Oui",
    no: "Non",
    rows: {
      connector: "Le connecteur DC Data dans Claude, avec les outils de votre équipe",
      "start-and-docs": "Accueil et Documentation",
      apps: "Les apps de votre équipe, comme Taux d’occupation",
      "own-switches": "Désactiver un outil pour vous-même, sur la page Outils",
      "admin-pages":
        "Les rôles, les équipes et le blocage d’une personne dans Personnes et équipes, qui obtient quel outil sur la page Outils, Statistiques et Journal d’activité",
      "issue-notes":
        "Lire l’historique d’un problème sous Échecs dans Statistiques et y ajouter une note",
      roadmap:
        "Suivre la feuille de route et y proposer des idées, avec l’avancement de chaque mois",
      "roadmap-budget":
        "Voir le budget de chaque mois et les heures et jours du travail sur la feuille de route, et modifier le budget pour ce mois-ci et les suivants",
      "roadmap-work":
        "Ajouter des idées directement à la feuille de route, examiner les idées et questions du Triage, fixer l’effort des éléments, les déplacer et voir les tickets Linear",
      "issue-work":
        "Prendre en charge, résoudre, ignorer ou rouvrir un problème sous Échecs dans Statistiques, et traiter les problèmes depuis Claude",
      "pause-tools": "Désactiver temporairement un outil pour tous, sur la page Outils",
      "view-as":
        "Voir le tableau de bord en tant qu'un collègue ou un rôle, sans pouvoir rien modifier, sous Voir en tant que dans Paramètres",
    },
    note: "Les administrateurs, propriétaires et développeurs font toujours partie de l’équipe Administrateurs, qui a tous les outils et toutes les apps.",
  },

  faq: {
    "not-listed": {
      question: "DC Data n’apparaît pas dans ma liste de connecteurs",
      answer: [
        "Un Owner de votre organisation Claude ajoute le connecteur une seule fois, pour tout le monde (voir [[connect-claude]]). Demandez-lui de l’ajouter.",
      ],
    },
    "sign-in-fails": {
      question: "La connexion échoue",
      answer: [
        "Connectez-vous avec votre propre compte Microsoft De Ceuster, votre compte de travail habituel. Les comptes invités et les comptes d’autres organisations sont refusés. Si la connexion échoue encore, demandez à un administrateur ou à Velta si votre compte est bloqué, ou à l’administrateur informatique de De Ceuster s’il est toujours actif.",
      ],
    },
    "not-from-odoo": {
      question: "Claude répond, mais pas à partir d’Odoo",
      answer: [
        "Une réponse issue d’Odoo affiche une étape DC Data, que vous pouvez ouvrir pour voir ce que Claude a consulté. Sans cette étape, la réponse ne vient pas d’Odoo. Vérifiez que le connecteur est activé pour la conversation, dans le menu des outils sous la zone de saisie.",
      ],
    },
    "cut-short": {
      question: "La réponse a été raccourcie",
      answer: [
        "Réduisez la portée de la question : une période plus courte, un seul client, une seule grue ou un seul type de commande. Ou demandez un résumé (chiffre d’affaires par mois) plutôt que chaque facture.",
      ],
    },
    slow: {
      question: "C’est long, ou Odoo est trop sollicité",
      answer: [
        "Odoo répond à un rythme limité : une grande question peut donc prendre une minute ou plus, et davantage quand plusieurs collègues posent des questions en même temps. Si Claude indique qu’Odoo était trop lent ou trop sollicité, réessayez après une minute.",
      ],
    },
    "number-wrong": {
      question: "Un chiffre semble incorrect",
      answer: [
        "Vérifiez les chiffres importants dans Odoo avant de les transmettre ou de prendre une décision sur cette base.",
        [
          "Le chiffre d’affaires et les montants des commandes sont hors TVA. Le total d’une facture et ce qui reste ouvert ou échu sont TVA comprise. Claude précise lequel des deux il donne.",
          "Le chiffre d’affaires exclut ce que vous facturez à vos propres sociétés De Ceuster ; la carte et la liste des clients excluent aussi ces sociétés.",
          "Les données dans Odoo commencent à la mi-2023 : comparez les années à partir de 2024.",
        ],
      ],
    },
    "how-looked-up": {
      question: "Comment Claude a-t-il cherché ?",
      answer: [
        "Demandez-le-lui : « Quelle période et quels filtres avez-vous utilisés ? » Vous voyez tout de suite si Claude a bien compris la question.",
      ],
    },
  },
};

/** The French, with its no-break spaces in place. */
export const fr: DocsContent = mapStrings(text, frenchSpacing);
